import React, { useState, useMemo, useEffect } from 'react';
import { DollarSign, ArrowUpRight, Copy, Check, RefreshCw, QrCode, Award, TrendingUp, Calendar, FileText, Download } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import API from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { downloadCertificatePdf } from '../lib/certificatePdf';
import { downloadAgreementPdf } from '../lib/agreementPdf';

const DEFAULT_PACKAGES = [
  { id: 1, price: 50, shares: 7000, monthly: 5 },
  { id: 2, price: 100, shares: 15000, monthly: 10 },
  { id: 3, price: 250, shares: 39000, monthly: 25 },
  { id: 4, price: 500, shares: 82000, monthly: 50 },
  { id: 5, price: 1000, shares: 180000, monthly: 100 },
  { id: 10, price: 1500, shares: 280000, monthly: 150 },
  { id: 6, price: 2000, shares: 380000, monthly: 200 },
  { id: 7, price: 5000, shares: 1050000, monthly: 500 },
  { id: 8, price: 10000, shares: 2500000, monthly: 1000 },
];

const fundingOptions = [
  'Full payment (Single allocation)',
  '10 monthly installments',
];

const fmt = (n) => n.toLocaleString('en-US');

const StatusChip = ({ status }) => {
  const map = {
    PENDING: 'bg-amber-100 text-amber-800 border-amber-500/40',
    IN_PROGRESS: 'bg-sky-100 text-sky-800 border-sky-500/40',
    APPROVED: 'bg-emerald-100 text-emerald-800 border-emerald-500/40',
    REJECTED: 'bg-red-100 text-red-800 border-red-500/40',
  };
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-widest ${map[status] || 'bg-emerald-100 text-emerald-900'}`}>
      <span className="h-1.5 w-1.5 rounded-full bg-current" /> {(status || '').replace('_', ' ')}
    </span>
  );
};

const Investments = ({ initialTab = 'packages', initialPkg }) => {
  const { toast } = useToast();
  const { user, refresh } = useAuth();
  const [tab, setTab] = useState(initialTab);
  const [selectedPkg, setSelectedPkg] = useState(initialPkg || 1);
  const [duration, setDuration] = useState(fundingOptions[0]);
  const [network, setNetwork] = useState('ERC20');
  const [txHash, setTxHash] = useState('');
  const [copied, setCopied] = useState(false);
  const [useBonus, setUseBonus] = useState(false);
  const [useMain, setUseMain] = useState(false);
  const [useKycBonus, setUseKycBonus] = useState(false);
  const [settings, setSettings] = useState({ usdt_erc20_address: '', usdt_erc20_qr: '', usdt_trc20_address: '', usdt_trc20_qr: '' });
  const [history, setHistory] = useState([]);
  const [kyc, setKyc] = useState(null);
  const [packages, setPackages] = useState(DEFAULT_PACKAGES);

  useEffect(() => {
    if (initialPkg) setSelectedPkg(initialPkg);
  }, [initialPkg]);

  const loadKyc = async () => {
    try { const { data } = await API.get('/kyc/me'); setKyc(data); } catch {}
  };

  const loadSettings = async () => {
    try { const { data } = await API.get('/settings'); setSettings(data); } catch {}
  };
  const loadHistory = async () => {
    try { const { data } = await API.get('/purchases/me'); setHistory(data.items || []); } catch {}
  };
  const loadPackages = async () => {
    try {
      const { data } = await API.get('/packages');
      if (Array.isArray(data.items) && data.items.length) setPackages(data.items);
    } catch {}
  };
  useEffect(() => { loadSettings(); loadHistory(); loadKyc(); loadPackages(); }, []);

  const pkg = useMemo(() => packages.find((p) => p.id === selectedPkg) || packages[0], [selectedPkg, packages]);
  const address = network === 'ERC20' ? settings.usdt_erc20_address : settings.usdt_trc20_address;
  const qrImg = network === 'ERC20' ? settings.usdt_erc20_qr : settings.usdt_trc20_qr;

  // ---- Wallet coverage math ----
  const isInstallment = /installment/i.test(duration || '');
  const dueAmount = isInstallment ? pkg.monthly : pkg.price;
  const freeBonus = Math.max(0, (user?.bonus_wallet || 0) - (user?.bonus_wallet_locked || 0));
  const mainBal = user?.main_wallet || 0;
  const kycBonusBal = user?.bonus_wallet_locked || 0;
  const walletCoverage =
    (useKycBonus ? kycBonusBal : 0) +
    (useBonus ? freeBonus : 0) +
    (useMain ? mainBal : 0);
  const remaining = Math.max(0, +(dueAmount - walletCoverage).toFixed(2));
  const walletCoversAll = remaining <= 0 && (useBonus || useMain || useKycBonus);

  const copyAddr = async () => {
    try {
      await navigator.clipboard.writeText(address || '');
      setCopied(true);
      toast({ title: 'Wallet address copied' });
      setTimeout(() => setCopied(false), 1600);
    } catch {
      toast({ title: 'Copy failed' });
    }
  };

  const submit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (!walletCoversAll) {
      return toast({
        title: 'Insufficient wallet balance',
        description: 'Top up your Main Wallet from Portal Center → Deposit and try again.',
      });
    }
    try {
      const { data } = await API.post('/purchases', {
        package_id: pkg.id,
        package_price: pkg.price,
        package_shares: pkg.shares,
        package_monthly: pkg.monthly,
        funding_duration: duration,
        network,
        tx_hash: 'WALLET_INTERNAL',
        use_bonus: useBonus,
        use_main: useMain,
        use_kyc_bonus: useKycBonus,
      });
      const auto = !!data?.auto_approved;
      toast({
        title: auto ? 'Package activated instantly' : 'Purchase confirmed via wallet',
        description: auto
          ? `Package ${pkg.id} — $${fmt(pkg.price)} paid from your wallet. Shares credited to your portfolio.`
          : `Package ${pkg.id} — $${fmt(pkg.price)} paid from your wallet balance.`,
      });
      setTxHash('');
      loadHistory();
      refresh();
    } catch (err) {
      toast({ title: 'Failed', description: err.response?.data?.detail || 'Please try again.' });
    }
  };

  return (
    <section className="px-4 md:px-10 py-6 pb-16">
      <div className="mx-auto max-w-[1240px]">
        <div className="rounded-3xl bg-[#fbf4dc] border border-emerald-900/15 shadow-2xl overflow-hidden">
          <div className="p-6 md:p-8 lg:p-10">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <h2 className="font-serif text-[30px] md:text-[34px] text-emerald-950 tracking-tight">Investments</h2>
              <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-800/40 bg-emerald-200/60 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-emerald-900">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-600" /> Real Estate
              </span>
            </div>

            <div className="mt-6 flex flex-wrap gap-2">
              <TabBtn active={tab === 'packages'} onClick={() => setTab('packages')}>Packages</TabBtn>
              <TabBtn active={tab === 'my'} onClick={() => setTab('my')}>My Investments</TabBtn>
              <TabBtn active={tab === 'installment'} onClick={() => setTab('installment')}>Installment Package</TabBtn>
              <TabBtn active={tab === 'certs'} onClick={() => setTab('certs')}>My Certificates</TabBtn>
            </div>

            <div className="mt-6">
              {tab === 'packages' && (
                <PackagesPanel
                  user={user}
                  pkg={pkg} setSelectedPkg={setSelectedPkg}
                  duration={duration} setDuration={setDuration}
                  network={network} setNetwork={setNetwork}
                  address={address} qrImg={qrImg}
                  copied={copied} copyAddr={copyAddr}
                  txHash={txHash} setTxHash={setTxHash}
                  useBonus={useBonus} setUseBonus={setUseBonus}
                  useMain={useMain} setUseMain={setUseMain}
                  useKycBonus={useKycBonus} setUseKycBonus={setUseKycBonus}
                  submit={submit}
                  dueAmount={dueAmount}
                  walletCoverage={walletCoverage}
                  remaining={remaining}
                  walletCoversAll={walletCoversAll}
                  packages={packages}
                />
              )}

              {tab === 'my' && (
                <Panel title="My Co-Ownership Allocations" icon={TrendingUp}>
                  {history.filter((h) => h.status === 'APPROVED').length === 0 ? (
                    <EmptyState text='No active allocations found on your account. Go to "Packages" to select an asset.' />
                  ) : (
                    <ul className="space-y-2">
                      {history.filter((h) => h.status === 'APPROVED').map((h) => (
                        <li key={h.id} className="rounded-xl border border-emerald-500/30 bg-emerald-50 px-4 py-3 flex items-center justify-between gap-3">
                          <div>
                            <div className="text-emerald-950 font-medium text-[14px]">Package {h.package_id} · ${fmt(h.package_price)}</div>
                            <div className="text-[12px] text-emerald-900/60">{fmt(h.package_shares)} shares · ${fmt(h.package_monthly)} monthly · {h.funding_duration}</div>
                          </div>
                          <StatusChip status={h.status} />
                        </li>
                      ))}
                    </ul>
                  )}
                </Panel>
              )}

              {tab === 'installment' && (
                <Panel title="Installment Payment Deck" icon={Calendar}>
                  <p className="text-sm text-emerald-900/70 -mt-2 mb-3">Track and submit your remaining monthly installments. Pay from wallet balance for instant activation, or via USDT for admin approval.</p>
                  <InstallmentDeck
                    plans={history.filter((h) => h.status === 'IN_PROGRESS' || (h.installment_total || 1) > 1)}
                    settings={settings}
                    user={user}
                    onSubmitted={() => { loadHistory(); refresh(); }}
                  />
                </Panel>
              )}

              {tab === 'certs' && (
                <Panel title="My Certificates" icon={FileText}>
                  {(() => {
                    const eligible = history.filter((h) => h.status === 'APPROVED');
                    const kycApproved = kyc?.status === 'APPROVED';
                    const holderName = kyc?.kyc ? `${kyc.kyc.firstName || ''} ${kyc.kyc.lastName || ''}`.trim() : '';
                    const country = kyc?.kyc?.country || '';

                    return (
                      <div className="space-y-4">
                        {/* Info banner — KYC gated */}
                        <div className={`rounded-xl border p-4 ${kycApproved ? 'border-emerald-500/30 bg-emerald-50' : 'border-amber-500/40 bg-amber-50'}`}>
                          <div className={`text-[14px] font-semibold ${kycApproved ? 'text-emerald-950' : 'text-amber-900'}`}>
                            Convertible Note Agreement + Share Certificate
                          </div>
                          <div className={`text-[12px] ${kycApproved ? 'text-emerald-900/70' : 'text-amber-900/80'}`}>
                            {kycApproved
                              ? 'Each approved package below unlocks its own personalised Agreement and Share Certificate — sealed & signed by SkywayGreen.LLC (Saint Lucia).'
                              : 'Complete and verify your KYC first to unlock your investor agreements and certificates.'}
                          </div>
                        </div>

                        {eligible.length === 0 ? (
                          <EmptyState text="No certificates issued yet. Certificates are minted after single-payment allocations are approved or all 10 monthly installments are completed." />
                        ) : (
                          <ul className="space-y-3">
                            {eligible.map((p) => (
                              <li key={p.id} className="rounded-xl border border-emerald-500/30 bg-emerald-50 px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
                                <div className="min-w-0">
                                  <div className="text-emerald-950 font-medium text-[14px]">
                                    Share Certificate · Package {p.package_id} · ${fmt(p.package_price)}
                                  </div>
                                  <div className="text-[12px] text-emerald-900/60">
                                    {fmt(p.package_shares)} Class A Equity Shares · {(p.installment_total || 1) > 1 ? '10 Monthly Installments (Completed)' : 'Single Payment'}
                                  </div>
                                </div>
                                <div className="flex items-center gap-2 flex-wrap">
                                  <button
                                    data-testid={`download-agreement-${p.id}`}
                                    onClick={() => downloadAgreementPdf({ kyc: { ...(kyc?.kyc || {}), email: kyc?.email }, purchase: p, joinDate: p.created_at || kyc?.created_at })}
                                    disabled={!kycApproved}
                                    title={kycApproved ? 'Download Convertible Note Agreement for this package' : 'Complete & verify KYC first'}
                                    className="inline-flex items-center gap-2 rounded-lg bg-emerald-900 hover:bg-emerald-800 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold px-4 py-2 transition"
                                  >
                                    <FileText size={14} /> Download Agreement
                                  </button>
                                  <button
                                    data-testid={`download-cert-${p.id}`}
                                    onClick={() => downloadCertificatePdf(p, holderName || 'Investor', country)}
                                    disabled={!holderName}
                                    title={holderName ? 'Download Share Certificate' : 'Complete & verify KYC first to unlock certificate name'}
                                    className="inline-flex items-center gap-2 rounded-lg bg-emerald-700 hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold px-4 py-2 transition"
                                  >
                                    <Download size={14} /> Download Certificate
                                  </button>
                                </div>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    );
                  })()}
                </Panel>
              )}
            </div>

            {/* Purchase History */}
            <div className="mt-8">
              <div className="flex items-center justify-between gap-3">
                <h3 className="font-serif text-emerald-950 text-xl">Purchase History</h3>
                <button
                  onClick={loadHistory}
                  className="inline-flex items-center gap-1.5 rounded-xl border border-emerald-900/20 bg-white/60 hover:bg-white text-emerald-900 text-sm font-medium px-3 py-2 transition"
                >
                  <RefreshCw size={14} /> Sync Log
                </button>
              </div>
              {history.length === 0 ? (
                <div className="mt-4 rounded-2xl border border-emerald-900/15 bg-[#fdf9e7] px-4 py-8 text-center text-sm text-emerald-900/65">
                  No dynamic purchases found on your account. Submit a request using the investment module.
                </div>
              ) : (
                <ul className="mt-4 space-y-2">
                  {history.map((h) => (
                    <li key={h.id} className="rounded-xl border border-emerald-900/15 bg-[#fdf9e7] px-4 py-3 flex items-center justify-between gap-3">
                      <div>
                        <div className="text-emerald-950 font-medium text-[14px]">Package {h.package_id} · ${fmt(h.package_price)} · <span className="text-emerald-700">{h.network}</span></div>
                        <div className="text-[12px] text-emerald-900/60 font-mono truncate max-w-[420px]">TX: {h.tx_hash}</div>
                        {h.status === 'REJECTED' && h.reason && (
                          <div className="mt-1 text-[12px] text-red-700">Reason: {h.reason}</div>
                        )}
                      </div>
                      <StatusChip status={h.status} />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const TabBtn = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    className={`rounded-xl px-4 py-2 text-sm font-medium transition ${active ? 'bg-emerald-950 text-emerald-50 shadow-sm' : 'bg-emerald-100/60 text-emerald-900 hover:bg-emerald-100'}`}
  >
    {children}
  </button>
);

const Panel = ({ title, icon: Icon, children }) => (
  <div className="rounded-2xl border border-emerald-900/15 bg-[#fdf9e7] p-5 md:p-6">
    <div className="flex items-center gap-2 mb-3">
      {Icon && <Icon size={16} className="text-emerald-700" />}
      <span className="text-emerald-900 font-semibold text-[15px]">{title}</span>
    </div>
    {children}
  </div>
);

const EmptyState = ({ text }) => (
  <div className="rounded-xl bg-emerald-950/[0.03] border border-dashed border-emerald-900/15 px-4 py-6 text-center text-sm text-emerald-900/65">
    {text}
  </div>
);

const PackagesPanel = ({ user, pkg, setSelectedPkg, duration, setDuration, network, setNetwork, address, qrImg, copied, copyAddr, txHash, setTxHash, useBonus, setUseBonus, useMain, setUseMain, useKycBonus, setUseKycBonus, submit, dueAmount, walletCoverage, remaining, walletCoversAll, packages }) => {
  return (
    <div className="rounded-2xl border border-emerald-900/25 bg-gradient-to-b from-[#062416] to-[#04160d] text-emerald-50 p-5 md:p-7">
      {/* Investment Deck header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-emerald-300 font-semibold">
            <DollarSign size={18} /> <span className="font-serif text-white text-xl">Investment Deck</span>
          </div>
          <p className="mt-1.5 text-[13px] text-emerald-100/70 max-w-xl">
            Select any funding target size card to configure the investment plan, view current deposit parameters, and submit for co-owner validation.
          </p>
        </div>
        <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/40 bg-emerald-500/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-emerald-200">
          <Award size={12} /> 10% Installments
        </span>
      </div>

      {/* Package grid */}
      <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {packages.map((p) => {
          const active = p.id === pkg.id;
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => setSelectedPkg(p.id)}
              className={`text-left rounded-xl border p-4 transition ${active ? 'border-emerald-400 bg-[#0a2416] ring-2 ring-emerald-500/30' : 'border-emerald-900/70 bg-[#061810]/70 hover:border-emerald-700 hover:bg-emerald-900/25'}`}
            >
              <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-emerald-300/80">Package {p.id}</div>
              <div className="mt-1 font-serif text-2xl text-emerald-300">${fmt(p.price)}.00</div>
              <div className="mt-0.5 text-[12px] text-emerald-100/75">{fmt(p.shares)} share units</div>
              <div className="mt-1 text-[11px] text-emerald-100/55">M/y: <span className="text-emerald-200">${fmt(p.monthly)}.00</span></div>
            </button>
          );
        })}
      </div>

      {/* Submit Allocation Request */}
      <div className="mt-8">
        <div className="text-emerald-300 font-serif text-lg font-semibold">Submit Allocation Request</div>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <FieldDark label="Selected Size">
            <select
              value={pkg.id}
              onChange={(e) => setSelectedPkg(Number(e.target.value))}
              className="dark-input appearance-none pr-8"
            >
              {packages.map((p) => (
                <option key={p.id} value={p.id} className="bg-[#04160d]">
                  ${fmt(p.price)}.00 — {fmt(p.shares)} shares
                </option>
              ))}
            </select>
          </FieldDark>
          <FieldDark label="Funding Duration">
            <select value={duration} onChange={(e) => setDuration(e.target.value)} className="dark-input appearance-none pr-8">
              {fundingOptions.map((f) => <option key={f} className="bg-[#04160d]">{f}</option>)}
            </select>
          </FieldDark>
        </div>

        {/* Summary strip — hidden per user request */}

        {/* Wallet Allocations */}
        <div className="mt-6">
          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-emerald-300 mb-2">Co-Funding Wallet Allocations</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <WalletTile
              label="Bonus Account"
              available={Math.max(0, (user?.bonus_wallet || 0) - (user?.bonus_wallet_locked || 0))}
              checked={useBonus}
              onChange={setUseBonus}
              testId="alloc-bonus"
            />
            <WalletTile
              label="Main Account"
              available={user?.main_wallet || 0}
              checked={useMain}
              onChange={setUseMain}
              testId="alloc-main"
            />
            <WalletTile
              label="KYC Bonus"
              available={user?.bonus_wallet_locked || 0}
              hint="Package purchase only"
              checked={useKycBonus}
              onChange={setUseKycBonus}
              testId="alloc-kyc-bonus"
            />
          </div>
        </div>

        {/* Payment summary — hidden per user request */}

        {/* Wallet coverage summary strip */}
        {(useBonus || useMain || useKycBonus) && (
          <div className="mt-5 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 flex items-center justify-between flex-wrap gap-3" data-testid="wallet-coverage-summary">
            <div className="text-[12px] text-emerald-100/85">
              <span className="uppercase tracking-widest text-emerald-300 font-semibold">
                {(/installment/i.test(duration || '')) ? 'First Installment Due' : 'Total Due'}:
              </span>
              <span className="ml-2 font-serif text-emerald-100 text-base">${fmt(dueAmount)}.00</span>
              <span className="mx-2 text-emerald-400/60">·</span>
              <span className="uppercase tracking-widest text-emerald-300 font-semibold">Wallet Coverage:</span>
              <span className="ml-2 font-serif text-emerald-100 text-base">${walletCoverage.toFixed(2)}</span>
            </div>
            {walletCoversAll ? (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-lime-400/50 bg-lime-400/15 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-lime-200">
                <Check size={12} /> Fully Covered — No TX Needed
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-400/50 bg-amber-400/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-amber-200">
                Remaining: ${remaining.toFixed(2)} USDT
              </span>
            )}
          </div>
        )}

        {/* Wallet doesn't cover — prompt user to top up first (USDT payment moved to the Deposit tab) */}
        {!walletCoversAll && (
          <div className="mt-6 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-4 text-[13px] text-amber-100" data-testid="wallet-shortfall-notice">
            <div className="font-semibold text-amber-200 uppercase tracking-[0.14em] text-[11px]">Insufficient Wallet Balance</div>
            <div className="mt-2 leading-relaxed">
              Your selected wallets cover <span className="font-semibold">${walletCoverage.toFixed(2)}</span> of the <span className="font-semibold">${fmt(dueAmount)}.00</span> due. You still need <span className="font-semibold text-amber-200">${remaining.toFixed(2)} USDT</span>.
            </div>
            <div className="mt-2 text-amber-100/85">
              Please go to <span className="font-semibold">Portal Center → Deposit</span> and top up your Main Wallet first, then return here to complete your package purchase from wallet balance.
            </div>
          </div>
        )}

        <button
          type="button"
          onClick={submit}
          disabled={!walletCoversAll}
          data-testid="submit-purchase-btn"
          className={`mt-6 w-full inline-flex items-center justify-center gap-2 rounded-xl font-semibold py-3.5 transition ${walletCoversAll ? 'bg-gradient-to-r from-lime-400 via-emerald-400 to-emerald-500 hover:brightness-110 text-emerald-950' : 'bg-emerald-900/40 text-emerald-100/40 cursor-not-allowed'}`}
        >
          Confirm & Pay from Wallet <ArrowUpRight size={16} />
        </button>
      </div>
    </div>
  );
};

const FieldDark = ({ label, children }) => (
  <label className="block">
    <span className="block text-[11px] font-semibold uppercase tracking-[0.14em] text-emerald-300 mb-2">{label}</span>
    <div className="relative">
      {children}
      <svg className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-emerald-300" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m6 9 6 6 6-6" /></svg>
    </div>
  </label>
);

const SumItem = ({ value, label, color }) => (
  <div className="text-center py-4 border-r last:border-r-0 border-emerald-900/50">
    <div className={`font-serif text-xl ${color || 'text-emerald-100'}`}>{value}</div>
    <div className="mt-0.5 text-[11px] text-emerald-100/60 uppercase tracking-wider">{label}</div>
  </div>
);

const WalletTile = ({ label, available, hint, checked, onChange, testId }) => {
  const hasBalance = Number(available) > 0;
  const effectiveChecked = hasBalance && checked;
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={() => hasBalance && onChange(!effectiveChecked)}
      disabled={!hasBalance}
      className={`text-left rounded-xl border p-4 flex items-start justify-between gap-3 transition ${
        !hasBalance ? 'border-emerald-900/40 bg-[#061810]/40 opacity-60 cursor-not-allowed' :
        effectiveChecked ? 'border-emerald-500 bg-emerald-500/10' :
        'border-emerald-900/60 bg-[#061810]/60 hover:bg-emerald-900/25'
      }`}
    >
      <div>
        <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-emerald-300">{label}</div>
        <div className="mt-1 text-[12px] text-emerald-100/60">Available:</div>
        <div className="text-white font-serif text-lg">${Number(available).toFixed(2)}</div>
        {hint && <div className="mt-1 text-[10px] text-amber-300/80 uppercase tracking-widest">{hint}</div>}
      </div>
      {hasBalance && (
        <span className={`mt-1 h-4 w-4 rounded border ${effectiveChecked ? 'bg-emerald-400 border-emerald-400' : 'border-emerald-700'}`}>
          {effectiveChecked && <Check size={14} className="text-emerald-950" />}
        </span>
      )}
    </button>
  );
};

const PayRow = ({ label, value, highlight }) => (
  <div className="flex items-center justify-between px-4 py-3">
    <div className="text-[13px] text-emerald-100/80">{label}</div>
    <div className={`font-serif text-lg ${highlight ? 'text-amber-400' : 'text-emerald-200'}`}>{value}</div>
  </div>
);

const NetTile = ({ active, onClick, name, desc }) => (
  <button
    type="button"
    onClick={onClick}
    className={`text-left rounded-xl border p-4 transition ${active ? 'border-emerald-400 bg-emerald-900/20 ring-2 ring-emerald-500/30' : 'border-emerald-900/60 bg-[#061810]/60 hover:bg-emerald-900/25'}`}
  >
    <div className="text-white font-medium text-[14px]">{name}</div>
    <div className="text-[12px] text-emerald-100/60">{desc}</div>
  </button>
);

const QrPattern = () => (
  <svg viewBox="0 0 21 21" width="100%" height="100%" shapeRendering="crispEdges" aria-hidden="true">
    <rect width="21" height="21" fill="#ffffff" />
    {/* Simple decorative QR - not scannable but visually similar */}
    {[
      '111111100101011111111',
      '100000101110001000001',
      '101110100001101011101',
      '101110100110101011101',
      '101110101010101011101',
      '100000101011101000001',
      '111111101010101111111',
      '000000001111100000000',
      '110101110001011011010',
      '011010011011000110001',
      '110001100100111101010',
      '011010010101100000101',
      '111011100011010110111',
      '000000001010011000010',
      '111111101110100010110',
      '100000100011101110100',
      '101110101010111011010',
      '101110100110110000111',
      '101110101010101101010',
      '100000100011011010001',
      '111111101101110110110',
    ].map((row, y) => row.split('').map((c, x) => c === '1' ? <rect key={`${x}-${y}`} x={x} y={y} width="1" height="1" fill="#04160d" /> : null))}
  </svg>
);

/* ---------- Installment Deck ---------- */
const InstallmentDeck = ({ plans, settings, user, onSubmitted }) => {
  const { toast } = useToast();
  if (!plans || plans.length === 0) {
    return <EmptyState text="No active installment plans. Choose a package with '10 monthly installments' funding to start one." />;
  }
  return (
    <div className="space-y-4">
      {plans.map((p) => (
        <InstallmentPlanCard key={p.id} plan={p} settings={settings} user={user} onSubmitted={onSubmitted} toast={toast} />
      ))}
    </div>
  );
};

const InstallmentPlanCard = ({ plan, settings, user, onSubmitted, toast }) => {
  const total = plan.installment_total || 10;
  const items = plan.installments || [];
  const approved = items.filter((i) => i.status === 'APPROVED').length;
  const hasPending = items.some((i) => i.status === 'PENDING');
  const nextMonth = approved + 1;
  const canSubmit = plan.status === 'IN_PROGRESS' && !hasPending && approved < total;

  // -------- Installment date helpers --------
  const anchorDate = plan.created_at ? new Date(plan.created_at) : new Date();
  const addMonths = (d, n) => { const x = new Date(d); x.setMonth(x.getMonth() + n); return x; };
  const dueDateFor = (monthNumber) => addMonths(anchorDate, monthNumber - 1);
  const dtFmt = (d) => d.toLocaleString(undefined, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  const dFmt = (d) => d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
  const daysBetween = (a, b) => Math.round((b - a) / (1000 * 60 * 60 * 24));
  const approvedItems = items.filter((i) => i.status === 'APPROVED' && (i.approved_at || i.processed_at));
  const getPaidAt = (i) => new Date(i.approved_at || i.processed_at);
  const lastPaid = approvedItems.length ? approvedItems.reduce((acc, i) => getPaidAt(i) > getPaidAt(acc) ? i : acc) : null;
  const nextDueDate = approved < total ? dueDateFor(nextMonth) : null;
  const nextDueRelative = nextDueDate ? (() => {
    const diff = daysBetween(new Date(), nextDueDate);
    if (diff === 0) return 'today';
    if (diff > 0) return `in ${diff} day${diff === 1 ? '' : 's'}`;
    return `overdue by ${Math.abs(diff)} day${Math.abs(diff) === 1 ? '' : 's'}`;
  })() : '';
  const [network, setNetwork] = React.useState('ERC20');
  const [txHash, setTxHash] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [useBonus, setUseBonus] = React.useState(false);
  const [useMain, setUseMain] = React.useState(false);
  const [useKycBonus, setUseKycBonus] = React.useState(false);
  const [autoPay, setAutoPay] = React.useState(!!plan.auto_pay);
  const [autoPayBusy, setAutoPayBusy] = React.useState(false);
  const address = network === 'ERC20' ? settings.usdt_erc20_address : settings.usdt_trc20_address;

  React.useEffect(() => { setAutoPay(!!plan.auto_pay); }, [plan.auto_pay]);

  const toggleAutoPay = async (nextEnabled) => {
    setAutoPayBusy(true);
    try {
      await API.patch(`/purchases/${plan.id}/auto-pay`, {
        enabled: nextEnabled,
        use_bonus: true,
        use_main: true,
        use_kyc_bonus: !!plan.auto_pay_use_kyc_bonus,
      });
      setAutoPay(nextEnabled);
      toast({
        title: nextEnabled ? 'Auto-pay enabled' : 'Auto-pay disabled',
        description: nextEnabled
          ? 'The next month will be paid automatically as soon as your wallet balance covers it.'
          : 'You will submit each installment manually.',
      });
      onSubmitted?.();
    } catch (e) {
      toast({ title: 'Failed', description: e.response?.data?.detail || 'Please try again.' });
    } finally { setAutoPayBusy(false); }
  };

  // ---- Wallet coverage math (mirrors package-purchase flow) ----
  const dueAmount = Number(plan.package_monthly) || 0;
  const kycBonusAvail = Number(user?.bonus_wallet_locked) || 0;
  const freeBonusAvail = Math.max(0, (Number(user?.bonus_wallet) || 0) - kycBonusAvail);
  const mainAvail = Number(user?.main_wallet) || 0;
  const walletCoverage =
    (useKycBonus ? kycBonusAvail : 0) +
    (useBonus ? freeBonusAvail : 0) +
    (useMain ? mainAvail : 0);
  const walletCoversAll = walletCoverage + 0.001 >= dueAmount && (useKycBonus || useBonus || useMain);

  const submit = async () => {
    if (!walletCoversAll) {
      return toast({
        title: 'Insufficient wallet balance',
        description: 'Top up your Main Wallet from Portal Center → Deposit and try again.',
      });
    }
    setBusy(true);
    try {
      const { data } = await API.post(`/purchases/${plan.id}/installment`, {
        tx_hash: 'WALLET_INTERNAL',
        network,
        use_bonus: useBonus,
        use_main: useMain,
        use_kyc_bonus: useKycBonus,
      });
      const auto = !!data?.auto_approved;
      toast({
        title: auto
          ? `Month ${nextMonth} activated instantly`
          : `Month ${nextMonth} submitted`,
        description: auto
          ? `$${fmt(plan.package_monthly)} paid from your wallet.`
          : 'Please wait a moment for confirmation.',
      });
      setTxHash('');
      setUseBonus(false); setUseMain(false); setUseKycBonus(false);
      onSubmitted?.();
    } catch (e) {
      toast({ title: 'Failed', description: e.response?.data?.detail || 'Please try again.' });
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-xl border border-emerald-900/15 bg-white/50 p-4 md:p-5">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-emerald-950 font-semibold">Package {plan.package_id} · ${fmt(plan.package_price)}</div>
          <div className="text-[12px] text-emerald-900/60">{fmt(plan.package_shares)} shares · ${fmt(plan.package_monthly)}/month · {plan.funding_duration}</div>
        </div>
        <StatusChip status={plan.status} />
      </div>

      {/* Progress dots */}
      <div className="mt-3 flex items-center gap-1.5 flex-wrap">
        {Array.from({ length: total }).map((_, idx) => {
          const it = items.find((i) => i.month === idx + 1);
          const st = it?.status || 'DUE';
          const color = st === 'APPROVED' ? 'bg-emerald-500 border-emerald-500' : st === 'PENDING' ? 'bg-amber-400 border-amber-400 animate-pulse' : st === 'REJECTED' ? 'bg-red-500 border-red-500' : 'bg-transparent border-emerald-900/30';
          const monthLabel = idx + 1;
          const tipDate = it?.approved_at || it?.processed_at
            ? `Paid on ${dtFmt(new Date(it.approved_at || it.processed_at))}`
            : `Due on ${dFmt(dueDateFor(monthLabel))}`;
          return (
            <div key={idx} className={`h-6 w-6 rounded-md border text-[10px] font-semibold flex items-center justify-center ${color} ${st === 'APPROVED' || st === 'PENDING' || st === 'REJECTED' ? 'text-white' : 'text-emerald-900/50'}`} title={`Month ${monthLabel}: ${st} · ${tipDate}`}>{monthLabel}</div>
          );
        })}
        <span className="ml-2 text-[12px] text-emerald-900/70">{approved}/{total} completed</span>
      </div>

      {/* Next installment date banner */}
      {plan.status === 'IN_PROGRESS' && approved < total && (
        <div
          className={`mt-3 rounded-lg border px-3 py-2 flex items-center justify-between flex-wrap gap-2 ${
            nextDueDate && nextDueDate < new Date()
              ? 'border-red-500/40 bg-red-50 text-red-900'
              : 'border-sky-500/40 bg-sky-50 text-sky-900'
          }`}
          data-testid={`next-installment-info-${plan.id}`}
        >
          <div className="text-[12.5px]">
            <span className="font-semibold">Next Installment (Month {nextMonth} of {total})</span>
            <span className="mx-1.5 opacity-60">·</span>
            <span>Due: <span className="font-semibold">{dFmt(nextDueDate)}</span></span>
            <span className="opacity-70"> ({nextDueRelative})</span>
          </div>
          {lastPaid && (
            <div className="text-[11.5px] opacity-80">
              Last paid: Month {lastPaid.month} on {dtFmt(getPaidAt(lastPaid))}
            </div>
          )}
        </div>
      )}

      {/* Auto-pay toggle */}
      {plan.status === 'IN_PROGRESS' && approved < total && (
        <div className="mt-3 flex items-center justify-between gap-3 rounded-lg border border-emerald-900/15 bg-white/60 px-3 py-2">
          <div className="min-w-0">
            <div className="text-emerald-950 font-medium text-[13px]">Auto-pay from wallet</div>
            <div className="text-[11px] text-emerald-900/60">Next months auto-pay from your Bonus / Main wallet whenever balance is enough.</div>
          </div>
          <button
            onClick={() => !autoPayBusy && toggleAutoPay(!autoPay)}
            disabled={autoPayBusy}
            className={`relative inline-flex h-6 w-11 shrink-0 rounded-full border transition ${autoPay ? 'bg-emerald-600 border-emerald-700' : 'bg-emerald-900/10 border-emerald-900/25'}`}
            data-testid={`installment-autopay-toggle-${plan.id}`}
            aria-pressed={autoPay}
          >
            <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition ${autoPay ? 'left-5' : 'left-0.5'}`} />
          </button>
        </div>
      )}

      {approved >= total && (
        <div className="mt-4 rounded-lg bg-emerald-100 border border-emerald-500/40 p-3 text-[13px] text-emerald-900">
          All installments completed. Your Share Certificate is now available in <span className="font-semibold">My Certificates</span>.
        </div>
      )}

      {plan.status === 'IN_PROGRESS' && hasPending && (
        <div className="mt-4 rounded-lg bg-amber-100 border border-amber-500/40 p-3 text-[13px] text-amber-900">
          Month {items.find((i) => i.status === 'PENDING')?.month} is under review by the admin. Please wait for approval to submit the next installment.
        </div>
      )}

      {canSubmit && (
        <div className="mt-4 rounded-lg border border-emerald-900/15 bg-[#fdf9e7] p-4">
          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-emerald-900/70">
            Submit Month {nextMonth} of {total} · Amount Due: <span className="text-emerald-800">${fmt(plan.package_monthly)}</span>
          </div>

          {/* Wallet Co-funding */}
          <div className="mt-3">
            <span className="block text-[11px] font-semibold uppercase tracking-[0.14em] text-emerald-900/70 mb-2">Pay from Wallet (optional)</span>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <InstWalletTile
                testId={`installment-bonus-${plan.id}`}
                title="Bonus Account"
                available={freeBonusAvail}
                checked={useBonus}
                onChange={setUseBonus}
              />
              <InstWalletTile
                testId={`installment-main-${plan.id}`}
                title="Main Account"
                available={mainAvail}
                checked={useMain}
                onChange={setUseMain}
              />
              <InstWalletTile
                testId={`installment-kyc-${plan.id}`}
                title="KYC Bonus"
                available={kycBonusAvail}
                checked={useKycBonus}
                onChange={setUseKycBonus}
                subtitle="Package purchase only"
              />
            </div>
            <div className="mt-2 flex items-center justify-between flex-wrap gap-2 text-[12px]">
              <div className="text-emerald-900/70">
                Wallet coverage: <span className="font-semibold text-emerald-950">${fmt(walletCoverage)}</span>
                {(useKycBonus || useBonus || useMain) && (
                  <> · Remaining: <span className={walletCoversAll ? 'text-emerald-700 font-semibold' : 'text-amber-800 font-semibold'}>${fmt(Math.max(0, dueAmount - walletCoverage))}</span></>
                )}
              </div>
              {walletCoversAll && (
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 border border-emerald-500/40 px-3 py-1 font-semibold text-emerald-900">
                  ✓ Fully covered — no TX needed
                </span>
              )}
            </div>
          </div>

          {/* Wallet shortfall notice — USDT payment moved to Deposit tab */}
          {!walletCoversAll && (
            <div className="mt-4 rounded-lg border border-amber-500/40 bg-amber-100/60 px-3 py-3 text-[12px] text-amber-900" data-testid={`installment-shortfall-${plan.id}`}>
              Wallet balance ${walletCoverage.toFixed(2)} does not cover the ${fmt(dueAmount)} installment. Please top up your Main Wallet from <span className="font-semibold">Portal Center → Deposit</span>, then return here.
            </div>
          )}

          <button
            onClick={submit}
            disabled={busy || !walletCoversAll}
            data-testid={`installment-submit-${plan.id}`}
            className={`mt-4 inline-flex items-center gap-2 rounded-xl font-semibold px-5 py-2.5 transition ${walletCoversAll ? 'bg-gradient-to-r from-lime-500 to-emerald-500 hover:brightness-110 text-emerald-950' : 'bg-emerald-900/20 text-emerald-900/40 cursor-not-allowed'}`}
          >
            {busy ? 'Submitting…' : `Pay Month ${nextMonth} from Wallet`} <ArrowUpRight size={14} />
          </button>
        </div>
      )}

      {items.some((i) => i.status === 'REJECTED') && (
        <div className="mt-3 text-[12px] text-red-700">
          Last rejection reason: {items.filter((i) => i.status === 'REJECTED').slice(-1)[0]?.reason || '—'}
        </div>
      )}
    </div>
  );
};

const InstWalletTile = ({ testId, title, available, checked, onChange, subtitle }) => {
  const hasBalance = Number(available) > 0;
  const effectiveChecked = hasBalance && checked;
  return (
    <label
      className={`block rounded-xl border p-3 transition ${
        !hasBalance ? 'border-emerald-900/15 bg-white/40 opacity-60 cursor-not-allowed' :
        effectiveChecked ? 'border-emerald-600 bg-emerald-100/60 ring-1 ring-emerald-500/50 cursor-pointer' :
        'border-emerald-900/15 bg-white/50 hover:border-emerald-900/30 cursor-pointer'
      }`}
      data-testid={testId}
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-emerald-900/70">{title}</div>
          <div className="mt-0.5 text-[11px] text-emerald-900/50">Available</div>
          <div className="text-emerald-950 font-serif text-xl">${fmt(available)}</div>
          {subtitle && <div className="mt-1 text-[10px] uppercase tracking-widest text-amber-700 font-semibold">{subtitle}</div>}
        </div>
        {hasBalance && (
          <input
            type="checkbox"
            checked={effectiveChecked}
            onChange={(e) => onChange(e.target.checked)}
            className="mt-1 h-4 w-4 accent-emerald-500"
          />
        )}
      </div>
    </label>
  );
};

export default Investments;
